import { useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isToday,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, ListTodo, MoreHorizontal, Instagram, Facebook } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { platformLabel, formatLabel, pubVisualFull } from "@/lib/constants";

const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

/* Teintes de carte par statut + pilules de format (Design System DGA) */
const STATUS_TINT = {
  a_valider: { bg: "#FFFBEB", border: "#FDE68A" },
  modification: { bg: "#FFF1F2", border: "#FECDD3" },
  valide: { bg: "#F0FDF4", border: "#BBF7D0" },
  programme: { bg: "#FFFFFF", border: "#E2E8F0" },
  publie: { bg: "#F8FAFC", border: "#E2E8F0" },
};
const FORMAT_PILL = {
  publication: { bg: "#E0F2FE", text: "#0369A1" },
  carrousel: { bg: "#F3E8FF", text: "#6B21A8" },
  reel: { bg: "#FFE4E6", text: "#BE123C" },
  story: { bg: "#FCE7F3", text: "#BE185D" },
  article_linkedin: { bg: "#DBEAFE", text: "#1D4ED8" },
};

const pubVisual = (p) => pubVisualFull(p).src;

/* Carte publication dans une cellule — vignette + titre + pilule format + heure/statut */
function PubCard({ p, color, readOnly, onPubClick, showThumb, tall }) {
  const [err, setErr] = useState(false);
  if (p.kind === "task") {
    return (
      <div data-testid={`cal-pub-${p.id}`} draggable={!readOnly}
        onDragStart={(e) => { e.dataTransfer.setData("pubId", p.id); e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); onPubClick?.(p); }}
        title={`Tâche : ${p.title}`}
        className="rounded-lg border border-dashed border-neutral-300 bg-white px-2 py-1 text-[11px] cursor-grab hover:shadow-sm transition-shadow">
        <div className="font-medium text-neutral-700 truncate flex items-center gap-1">
          <ListTodo className="w-3 h-3 shrink-0 text-neutral-400" />{p.title}
        </div>
        <p className="text-[10px] text-neutral-400 mt-0.5">Tâche{p.client_name ? ` · ${p.client_name}` : ""}</p>
      </div>
    );
  }
  const tint = STATUS_TINT[p.status] || STATUS_TINT.programme;
  const pill = FORMAT_PILL[p.format] || { bg: "#F1F5F9", text: "#475569" };
  const visual = showThumb ? pubVisual(p) : null;
  return (
    <div
      data-testid={`cal-pub-${p.id}`}
      draggable={!readOnly}
      onDragStart={(e) => { e.dataTransfer.setData("pubId", p.id); e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); onPubClick?.(p); }}
      title={`${p.title} — ${platformLabel(p.platform)} ${formatLabel(p.format)}`}
      className="rounded-xl border px-2 py-2 text-[11px] leading-tight cursor-grab hover:shadow-md transition-shadow"
      style={{ background: tint.bg, borderColor: tint.border, borderLeft: `3px solid ${color}` }}
    >
      <div className="flex items-start gap-2">
        {visual && !err ? (
          pubVisualFull(p).video ? (
            <video src={visual} muted playsInline preload="metadata" onError={() => setErr(true)}
              data-testid={`cal-pub-thumb-${p.id}`}
              className={`${tall ? "w-12 h-12" : "w-9 h-9"} rounded-lg object-cover shrink-0 border border-black/5`} />
          ) : (
            <img src={visual} alt="" loading="lazy" onError={() => setErr(true)}
              data-testid={`cal-pub-thumb-${p.id}`}
              className={`${tall ? "w-12 h-12" : "w-9 h-9"} rounded-lg object-cover shrink-0 border border-black/5`} />
          )
        ) : showThumb && (
          <span className={`${tall ? "w-12 h-12" : "w-9 h-9"} rounded-lg shrink-0 border border-black/5`} style={{ background: `${color}22` }} />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[#0F172A] leading-snug line-clamp-2">{p.title}</p>
          <span className="inline-block mt-1 text-[9px] font-semibold rounded-full px-2 py-0.5"
            style={{ background: pill.bg, color: pill.text }}>
            {formatLabel(p.format)}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-1.5 mt-1.5 overflow-hidden">
        <span className="text-[10px] text-neutral-400 font-medium truncate min-w-0 flex items-center gap-1">
          {(() => {
            const ch = p.channels && Object.values(p.channels).some(Boolean)
              ? p.channels
              : { instagram: p.platform !== "facebook", facebook: p.platform === "facebook" };
            return (
              <>
                {ch.instagram && <Instagram className="w-3 h-3 shrink-0 text-[#E1306C]" data-testid={`cal-pub-ig-${p.id}`} />}
                {ch.facebook && <Facebook className="w-3 h-3 shrink-0 text-[#1877F2]" data-testid={`cal-pub-fb-${p.id}`} />}
              </>
            );
          })()}
          {p.time}{!showThumb && p.client_name ? ` · ${p.client_name}` : ""}
        </span>
        <StatusBadge status={p.status} short className="!px-1.5 !py-0 !text-[9px] shrink-0" />
      </div>
    </div>
  );
}

export default function CalendarMonth({ pubs, colorOf, onPubClick, onDayClick, onMove, month, setMonth, readOnly = false, view = "month", thumbnails = true, hideHeader = false }) {
  const [dragOver, setDragOver] = useState(null);
  const [dayPopup, setDayPopup] = useState(null);

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    const rows = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return rows;
  }, [month]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(month, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [month]);

  const byDay = useMemo(() => {
    const map = {};
    pubs.forEach((p) => { if (p.date) (map[p.date] = map[p.date] || []).push(p); });
    return map;
  }, [pubs]);

  const handleDrop = (e, day) => {
    e.preventDefault();
    setDragOver(null);
    if (readOnly) return;
    const id = e.dataTransfer.getData("pubId");
    if (id) onMove?.(id, format(day, "yyyy-MM-dd"));
  };

  const navigate = (dir) => {
    if (view === "week") setMonth(addDays(month, dir * 7));
    else setMonth(addMonths(month, dir));
  };

  const renderCell = (day, { tall = false } = {}) => {
    const key = format(day, "yyyy-MM-dd");
    const dayPubs = byDay[key] || [];
    const maxShown = tall ? 4 : 2;
    const today = isToday(day);
    const inMonth = view === "week" || isSameMonth(day, month);
    return (
      <div
        key={key}
        data-testid={`cal-day-${key}`}
        onDragOver={(e) => { if (!readOnly) { e.preventDefault(); setDragOver(key); } }}
        onDragLeave={() => setDragOver(null)}
        onDrop={(e) => handleDrop(e, day)}
        onClick={() => !readOnly && onDayClick?.(key)}
        className={`cal-day ${tall ? "min-h-[360px]" : "min-h-[150px]"} p-2 border-r border-neutral-100 last:border-0 align-top group transition-colors ${
          !inMonth ? "bg-neutral-50/60" : today ? "bg-[#E0F2FE]/25" : ""} ${dragOver === key ? "ring-2 ring-inset ring-[#0B132B]/30 bg-[#E0F2FE]/50" : ""} ${!readOnly ? "cursor-pointer" : ""}`}
      >
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
            today ? "bg-[#0B132B] text-white shadow" : inMonth ? "text-neutral-500" : "text-neutral-300"}`}>
            {format(day, "d")}
          </span>
          <div className="flex items-center gap-0.5">
            {!readOnly && <Plus className="w-3.5 h-3.5 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
            {dayPubs.length > 0 && (
              <button data-testid={`cal-day-menu-${key}`} title="Voir tout le jour"
                onClick={(e) => { e.stopPropagation(); setDayPopup(key); }}
                className="p-0.5 rounded text-neutral-300 hover:text-neutral-600 hover:bg-neutral-100 opacity-0 group-hover:opacity-100 transition-all">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="space-y-1.5 mt-1.5">
          {dayPubs.slice(0, maxShown).map((p) => (
            <PubCard key={p.id} p={p} color={colorOf(p)} readOnly={readOnly} onPubClick={onPubClick} showThumb={thumbnails} tall={tall} />
          ))}
          {dayPubs.length > maxShown && (
            <button
              data-testid={`cal-more-${key}`}
              onClick={(e) => { e.stopPropagation(); setDayPopup(key); }}
              className="text-[10px] font-medium text-[#0369A1] hover:underline px-1 transition-colors">
              +{dayPubs.length - maxShown} autres
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="calendar-month">
      {!hideHeader && (
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
          <h2 className="font-heading font-bold text-lg text-[#0F172A] capitalize" data-testid="calendar-month-label">
            {format(month, "MMMM yyyy", { locale: fr })}
          </h2>
          <div className="flex gap-1">
            <button data-testid="cal-prev" onClick={() => navigate(-1)}
              className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
            <button data-testid="cal-today" onClick={() => setMonth(new Date())}
              className="px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-neutral-100 text-neutral-600 transition-colors">Aujourd'hui</button>
            <button data-testid="cal-next" onClick={() => navigate(1)}
              className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-7 text-[11px] font-semibold text-neutral-500 border-b border-neutral-100">
        {DAY_NAMES.map((d) => (
          <div key={d} className="px-3 py-2.5">{d}</div>
        ))}
      </div>
      {view === "week" ? (
        <div className="grid grid-cols-7" data-testid="calendar-week-grid">
          {weekDays.map((day) => renderCell(day, { tall: true }))}
        </div>
      ) : (
        weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-neutral-100 last:border-0">
            {week.map((day) => renderCell(day))}
          </div>
        ))
      )}
    </div>

    <Dialog open={!!dayPopup} onOpenChange={(v) => !v && setDayPopup(null)}>
      <DialogContent className="max-w-md" data-testid="day-pubs-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading capitalize">
            {dayPopup && format(new Date(dayPopup + "T12:00:00"), "EEEE d MMMM yyyy", { locale: fr })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {(byDay[dayPopup] || []).map((p) => (
            <button key={p.id} data-testid={`day-pub-${p.id}`}
              onClick={() => { setDayPopup(null); onPubClick?.(p); }}
              className="w-full text-left border border-[#E2E8F0] rounded-xl px-3.5 py-2.5 hover:bg-neutral-50 transition-colors"
              style={{ borderLeft: `3px solid ${colorOf(p)}` }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium flex-1 truncate">{p.time} · {p.title}</span>
                <StatusBadge status={p.status} className="!text-[10px]" />
              </div>
              <p className="text-xs text-neutral-400 mt-0.5">{p.client_name} · {platformLabel(p.platform)} · {formatLabel(p.format)}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
